import React, { useState, useEffect } from 'react';
import { APIKey } from '../../types/apiKeys';
import { apiKeyService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { CreateAPIKeyModal } from './CreateAPIKeyModal';
import { RegenerateAPIKeyModal } from './RegenerateAPIKeyModal';
import { NewAPIKeyModal } from './NewAPIKeyModal';
import { TestAPIKeyModal } from './TestAPIKeyModal';

interface APIKeyListProps {
  onRefresh?: () => void;
}

export const APIKeyList: React.FC<APIKeyListProps> = ({ onRefresh }) => {
  const { showToast } = useToast();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null);
  const [newApiKey, setNewApiKey] = useState<{
    api_key: string;
    secret_key: string;
    auth_token: string;
  } | null>(null);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      const response = await apiKeyService.getAPIKeys();
      setApiKeys(response.results || response);
    } catch (error) {
      showToast('Failed to load API keys', 'error');
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAPIKey = async (apiKeyData: any) => {
    try {
      const response = await apiKeyService.createAPIKey(apiKeyData);
      setNewApiKey({
        api_key: response.api_key,
        secret_key: response.secret_key,
        auth_token: response.auth_token
      });
      setShowCreateModal(false);
      loadAPIKeys();
      onRefresh?.();
      showToast('API key created successfully', 'success');
    } catch (error) {
      showToast('Failed to create API key', 'error');
      console.error('Error creating API key:', error);
    }
  };

  const handleRevokeAPIKey = async (apiKey: APIKey) => {
    if (!window.confirm(`Are you sure you want to revoke "${apiKey.name}"?`)) {
      return;
    }

    try {
      await apiKeyService.revokeAPIKey(apiKey.id);
      showToast('API key revoked successfully', 'success');
      loadAPIKeys();
      onRefresh?.();
    } catch (error) {
      showToast('Failed to revoke API key', 'error');
      console.error('Error revoking API key:', error);
    }
  };

  const handleRegenerateAPIKey = async (apiKey: APIKey) => {
    if (!window.confirm(`Are you sure you want to regenerate "${apiKey.name}"? This will invalidate the current key.`)) {
      return;
    }

    try {
      const response = await apiKeyService.regenerateAPIKey(apiKey.id);
      setNewApiKey({
        api_key: response.new_api_key.api_key,
        secret_key: response.new_api_key.secret_key,
        auth_token: response.new_api_key.auth_token
      });
      setShowRegenerateModal(false);
      setSelectedKey(null);
      loadAPIKeys();
      onRefresh?.();
      showToast('API key regenerated successfully', 'success');
    } catch (error) {
      showToast('Failed to regenerate API key', 'error');
      console.error('Error regenerating API key:', error);
    }
  };

  const handleDeleteAPIKey = async (apiKey: APIKey) => {
    if (!window.confirm(`Are you sure you want to delete "${apiKey.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiKeyService.deleteAPIKey(apiKey.id);
      showToast('API key deleted successfully', 'success');
      loadAPIKeys();
      onRefresh?.();
    } catch (error) {
      showToast('Failed to delete API key', 'error');
      console.error('Error deleting API key:', error);
    }
  };

  const getStatusBadge = (apiKey: APIKey) => {
    const statusColors = {
      active: 'bg-green-100 dark:bg-green-900/30 text-green-800',
      inactive: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100',
      expired: 'bg-red-100 dark:bg-red-900/30 text-red-800',
      revoked: 'bg-red-100 dark:bg-red-900/30 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[apiKey.status]}`}>
        {apiKey.status.charAt(0).toUpperCase() + apiKey.status.slice(1)}
      </span>
    );
  };

  const getTypeBadge = (apiKey: APIKey) => {
    const typeColors = {
      read: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800',
      write: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800',
      admin: 'bg-red-100 dark:bg-red-900/30 text-red-800'
    };

    const typeLabels = {
      read: 'Read Only',
      write: 'Read & Write',
      admin: 'Full Access'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[apiKey.key_type]}`}>
        {typeLabels[apiKey.key_type]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">API Keys</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create New API Key
        </button>
      </div>

      {apiKeys.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No API keys found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Create your first API key to start using the API</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create API Key
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {apiKeys.map((apiKey) => (
            <div key={apiKey.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{apiKey.name}</h3>
                    {getStatusBadge(apiKey)}
                    {getTypeBadge(apiKey)}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 dark:text-gray-300 mb-4">
                    <div>
                      <span className="font-medium">API Key:</span>
                      <div className="font-mono text-xs bg-gray-100 dark:bg-gray-800 p-1 rounded mt-1">
                        {apiKey.display_key}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Rate Limit:</span>
                      <div>{apiKey.rate_limit} requests/hour</div>
                    </div>
                    <div>
                      <span className="font-medium">Remaining:</span>
                      <div>{apiKey.remaining_requests} requests</div>
                    </div>
                    <div>
                      <span className="font-medium">Total Requests:</span>
                      <div>{apiKey.total_requests}</div>
                    </div>
                  </div>

                  {apiKey.expires_at && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      <span className="font-medium">Expires:</span> {new Date(apiKey.expires_at).toLocaleDateString()}
                    </div>
                  )}

                  {apiKey.last_used_at && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      <span className="font-medium">Last Used:</span> {new Date(apiKey.last_used_at).toLocaleString()}
                    </div>
                  )}
                </div>

                                 <div className="flex flex-col gap-2 ml-4">
                   <button
                     onClick={() => {
                       setSelectedKey(apiKey);
                       setShowTestModal(true);
                     }}
                     className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:bg-green-900/30"
                   >
                     Test
                   </button>
                   <button
                     onClick={() => {
                       setSelectedKey(apiKey);
                       setShowRegenerateModal(true);
                     }}
                     className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                   >
                     Regenerate
                   </button>
                   <button
                     onClick={() => handleRevokeAPIKey(apiKey)}
                     disabled={apiKey.status === 'revoked'}
                     className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                   >
                     Revoke
                   </button>
                   <button
                     onClick={() => handleDeleteAPIKey(apiKey)}
                     className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300"
                   >
                     Delete
                   </button>
                 </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <CreateAPIKeyModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateAPIKey}
        />
      )}

      {/* Regenerate API Key Modal */}
      {showRegenerateModal && selectedKey && (
        <RegenerateAPIKeyModal
          apiKey={selectedKey}
          onClose={() => {
            setShowRegenerateModal(false);
            setSelectedKey(null);
          }}
          onSubmit={handleRegenerateAPIKey}
        />
      )}

             {/* New API Key Display Modal */}
       {newApiKey && (
         <NewAPIKeyModal
           apiKey={newApiKey}
           onClose={() => setNewApiKey(null)}
         />
       )}

       {/* Test API Key Modal */}
       {showTestModal && selectedKey && (
         <TestAPIKeyModal
           apiKey={selectedKey}
           onClose={() => {
             setShowTestModal(false);
             setSelectedKey(null);
           }}
         />
       )}
     </div>
   );
 };
